import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

export type FaceDelegate = 'GPU' | 'CPU';
export type FaceDelegateMode = FaceDelegate | 'AUTO';

let instance: FaceLandmarker | null = null;
let instanceDelegate: FaceDelegate | null = null;
let loading: Promise<FaceLandmarker> | null = null;
let filesetPromise: Promise<unknown> | null = null;
let preloadPromise: Promise<void> | null = null;

function readMaxFaces(): number {
  const raw = String(import.meta.env.VITE_MAX_FACES ?? '').trim();
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.max(1, Math.floor(n));
  return 5;
}

function isIOSLike(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || (navigator as unknown as { vendor?: string }).vendor || '';
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  const n = navigator as Navigator & { maxTouchPoints?: number };
  // iPadOS sometimes reports as Mac but with touch.
  if (
    (navigator.platform === 'MacIntel' || /Mac OS X/.test(ua)) &&
    typeof n.maxTouchPoints === 'number' &&
    n.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

function isFirefoxLike(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Firefox\//i.test(navigator.userAgent || '');
}

function readDelegateModeOverride(): FaceDelegateMode | null {
  const raw = String(import.meta.env.VITE_FACE_DELEGATE ?? '')
    .trim()
    .toLowerCase();
  if (!raw) return null;
  if (raw === 'auto') return 'AUTO';
  if (raw === 'gpu') return 'GPU';
  if (raw === 'cpu') return 'CPU';
  return null;
}

function pickDelegateOrder(mode: FaceDelegateMode): FaceDelegate[] {
  if (mode === 'CPU') return ['CPU'];
  if (mode === 'GPU') return ['GPU'];
  // AUTO: prefer CPU on Firefox + iOS/WebKit for compatibility; elsewhere try GPU first.
  return isIOSLike() || isFirefoxLike() ? ['CPU', 'GPU'] : ['GPU', 'CPU'];
}

function resolveModelPath(base: string, p: string): string {
  if (/^https?:\/\//i.test(p) || p.startsWith('/')) return p;
  if (p.includes('/')) return `${base}${p}`;
  return `${base}models/${p}`;
}

function readModelCandidates(base: string, override?: string): string[] {
  const raw = String(override ?? import.meta.env.VITE_FACE_MODEL ?? '').trim();
  const out: string[] = [];
  const add = (p: string) => {
    if (!out.includes(p)) out.push(p);
  };
  if (raw) add(resolveModelPath(base, raw));

  // Default model (landmarks-only or landmarks+extras depending on the file you ship).
  add(`${base}models/face_landmarker.task`);

  return out;
}

function readBooleanEnv(name: string, fallback = false): boolean {
  const raw = String(import.meta.env[name] ?? '').trim().toLowerCase();
  if (!raw) return fallback;
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

function toErrorString(e: unknown): string {
  if (e instanceof Error) return e.message || String(e);
  return String(e);
}

export function getActiveFaceDelegate(): FaceDelegate | null {
  return instanceDelegate;
}

export function preloadFaceAssets(): Promise<void> {
  if (preloadPromise) return preloadPromise;
  const base = import.meta.env.BASE_URL || '/';
  const urls = [
    `${base}wasm/vision_wasm_internal.js`,
    `${base}wasm/vision_wasm_internal.wasm`,
    `${base}models/face_landmarker.task`,
  ];
  preloadPromise = Promise.all(
    urls.map((url) =>
      fetch(url, { cache: 'force-cache' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
        })
        .catch((error) => {
          console.warn(`[Face] preload failed for ${url}`, error);
        }),
    ),
  ).then(() => undefined);
  return preloadPromise;
}

export async function getFaceLandmarker(opts?: {
  delegate?: FaceDelegateMode;
  forceReload?: boolean;
  model?: string;
  enableExtras?: boolean;
}): Promise<FaceLandmarker> {
  const requestedMode = readDelegateModeOverride() ?? opts?.delegate ?? 'AUTO';
  const forceReload = opts?.forceReload === true;

  const requiredDelegate: FaceDelegate | null = requestedMode === 'AUTO' ? null : requestedMode;

  if (!forceReload && instance && (!requiredDelegate || instanceDelegate === requiredDelegate)) {
    return instance;
  }

  const base = import.meta.env.BASE_URL || '/';
  const wasmBaseUrl = `${base}wasm`;
  const modelCandidates = readModelCandidates(base, opts?.model);
  const maxFaces = readMaxFaces();

  if (loading) {
    // If another load is in-flight, await it first; then decide if we still need to reload.
    const current = await loading.catch(() => null);
    if (!forceReload && current && (!requiredDelegate || instanceDelegate === requiredDelegate)) {
      return current;
    }
  }

  const delegates = requiredDelegate ? [requiredDelegate] : pickDelegateOrder(requestedMode);
  const wantsExtras = opts?.enableExtras ?? readBooleanEnv('VITE_FACE_ENABLE_EXTRAS', false);
  const extrasOrder = wantsExtras ? ([true, false] as const) : ([false] as const);

  loading = (async () => {
    try {
      // Cache the fileset promise so switching delegates doesn't refetch wasm JS repeatedly.
      if (!filesetPromise) {
        filesetPromise = FilesetResolver.forVisionTasks(wasmBaseUrl);
      }
      const fileset = (await filesetPromise) as Parameters<typeof FaceLandmarker.createFromOptions>[0];

      let lastErr: unknown = null;
      for (const enableExtras of extrasOrder) {
        for (const modelAssetPath of modelCandidates) {
          for (const delegate of delegates) {
            try {
              const lm = await FaceLandmarker.createFromOptions(fileset, {
                baseOptions: { modelAssetPath, delegate },
                runningMode: 'VIDEO',
                numFaces: maxFaces,
                outputFaceBlendshapes: enableExtras,
                outputFacialTransformationMatrixes: enableExtras,
              });

              const prev = instance;
              instance = lm;
              instanceDelegate = delegate;
              if (prev && prev !== lm) {
                try {
                  prev.close();
                } catch {
                  // ignore close errors
                }
              }

              if (delegate !== 'GPU') {
                console.warn('[Face] GPU delegate unavailable; using CPU.');
              }
              if (wantsExtras && !enableExtras) {
                console.warn(
                  '[Face] Blendshapes/transform outputs unavailable; continuing with landmarks-only. (Hint: ship a blendshape-capable .task model.)',
                );
              }

              return lm;
            } catch (e) {
              lastErr = e;
              console.warn(
                `[Face] init failed (delegate=${delegate}, extras=${enableExtras}, model=${modelAssetPath})`,
                e,
              );
            }
          }
        }
      }
      throw lastErr ?? new Error('FaceLandmarker initialization failed');
    } finally {
      // Allow subsequent calls even if this load fails.
      // (If it succeeds, instance is already cached.)
      loading = null;
    }
  })();

  return loading;
}

export function releaseFaceLandmarker(): void {
  if (instance) {
    instance.close();
    instance = null;
    instanceDelegate = null;
  }
  loading = null;
  filesetPromise = null;
}

export async function warmupFaceLandmarker(video?: HTMLVideoElement): Promise<void> {
  const lm = await getFaceLandmarker();
  if (video) {
    try {
      lm.detectForVideo(video, performance.now());
    } catch {
      // ignore if video not ready
    }
  }
}

export async function waitForFace(
  video: HTMLVideoElement,
  lm: FaceLandmarker,
  timeoutMs = 4000,
): Promise<boolean> {
  const start = performance.now();
  return new Promise((resolve) => {
    function tick() {
      const now = performance.now();
      if (video.readyState < 2) {
        if (now - start > timeoutMs) return resolve(false);
        requestAnimationFrame(tick);
        return;
      }
      const res = lm.detectForVideo(video, now);
      if (res.faceLandmarks && res.faceLandmarks.length > 0) return resolve(true);
      if (now - start > timeoutMs) return resolve(false);
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

export async function forceFaceDelegate(delegate: FaceDelegate): Promise<FaceLandmarker> {
  try {
    return await getFaceLandmarker({ delegate, forceReload: true });
  } catch (e) {
    throw new Error(`切换人脸引擎失败（${delegate}）：${toErrorString(e)}`);
  }
}
